// Hours engine for the Meals tab. Restaurant.hours_structured is keyed
// by abbreviated weekday (mon/tue/wed/thu/fri/sat/sun); each value is
// either null (closed) or { open: 'HH:MM', close: 'HH:MM' }. close <
// open is a wrap-into-tomorrow window (e.g. Pel'meni Fri 11:30 → 03:30
// Saturday morning).
//
// All comparisons happen in the trip's timezone — never use the
// browser's local clock directly. Returns minute-precision booleans;
// no second-precision needed for restaurant hours.

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function tzParts(date, tz) {
  // Use Intl.DateTimeFormat to get the local hour/minute/dow in tz.
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  const parts = fmt.formatToParts(date);
  const get = (k) => parts.find((p) => p.type === k)?.value;
  const weekday = (get('weekday') || '').toLowerCase().slice(0, 3); // sun..sat
  const hour = parseInt(get('hour') || '0', 10);
  const minute = parseInt(get('minute') || '0', 10);
  return { weekday, minutes: hour * 60 + minute };
}

function parseHM(hm) {
  if (!hm) return null;
  const [h, m] = hm.split(':').map((s) => parseInt(s, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function prevDayKey(day) {
  const idx = DAY_KEYS.indexOf(day);
  return DAY_KEYS[(idx + 6) % 7];
}

function nextDayKey(day) {
  const idx = DAY_KEYS.indexOf(day);
  return DAY_KEYS[(idx + 1) % 7];
}

// Returns { open: bool, label: 'Open · closes 22:00' | 'Closed · opens
// 11AM Wed' | 'Closed · opens later this week' }.
export function openStatus(hoursStructured, now, tz) {
  if (!hoursStructured) return { open: false, label: 'Hours unknown' };
  const { weekday, minutes } = tzParts(now, tz);
  const today = hoursStructured[weekday];

  // Wrap-from-yesterday: if yesterday's window crosses midnight and
  // we're still inside it (e.g. it's 01:00 Friday and Thursday was
  // 11:30→01:30), we're still open from Thursday's perspective.
  const yesterday = hoursStructured[prevDayKey(weekday)];
  if (yesterday && yesterday.close && yesterday.open) {
    const yOpen = parseHM(yesterday.open);
    const yClose = parseHM(yesterday.close);
    if (yOpen != null && yClose != null && yClose < yOpen) {
      // Wraps. Yesterday's "close" lands at minutes-of-today.
      if (minutes < yClose) {
        return { open: true, label: `Open · closes ${formatHM(yClose)} (carryover)` };
      }
    }
  }

  if (today) {
    const open = parseHM(today.open);
    const close = parseHM(today.close);
    if (open != null && close != null) {
      const wraps = close < open;
      const inWindow = wraps
        ? minutes >= open // before midnight portion of a wrap window
        : minutes >= open && minutes < close;
      if (inWindow) {
        return {
          open: true,
          label: wraps
            ? `Open · closes ${formatHM(close)} ${nextDayKey(weekday).toUpperCase()}`
            : `Open · closes ${formatHM(close)}`,
        };
      }
      // Closed but opens later today
      if (minutes < open) {
        return { open: false, label: `Closed · opens ${formatHM(open)} today` };
      }
    }
  }

  // Find the next open day in the week. Start from tomorrow.
  for (let i = 1; i <= 7; i += 1) {
    const idx = (DAY_KEYS.indexOf(weekday) + i) % 7;
    const day = DAY_KEYS[idx];
    const dayHours = hoursStructured[day];
    if (dayHours?.open) {
      return {
        open: false,
        label: `Closed · opens ${formatHM(parseHM(dayHours.open))} ${day.toUpperCase()}`,
      };
    }
  }
  return { open: false, label: 'Closed' };
}

export function isOpenNow(hoursStructured, now, tz) {
  return openStatus(hoursStructured, now, tz).open;
}

function formatHM(minutesOfDay) {
  if (minutesOfDay == null) return '';
  const h = Math.floor(minutesOfDay / 60);
  const m = minutesOfDay % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  if (m === 0) return `${h12}${ampm}`;
  return `${h12}:${String(m).padStart(2, '0')}${ampm}`;
}

// Render a one-line summary of the full week's hours. Used in the
// "By Type" view where the slot context isn't there.
export function summarizeWeek(hoursStructured) {
  if (!hoursStructured) return 'hours unknown';
  // Group consecutive days with identical hours.
  const groups = [];
  for (const day of DAY_KEYS.slice(1).concat(DAY_KEYS[0])) {
    // Order Mon-Sun for display
    const h = hoursStructured[day];
    const sig = h ? `${h.open}-${h.close}` : 'closed';
    const last = groups[groups.length - 1];
    if (last && last.sig === sig) {
      last.days.push(day);
    } else {
      groups.push({ sig, days: [day] });
    }
  }
  return groups
    .map((g) => {
      const range =
        g.days.length === 1
          ? g.days[0].toUpperCase()
          : `${g.days[0].toUpperCase()}-${g.days[g.days.length - 1].toUpperCase()}`;
      return `${range} ${g.sig === 'closed' ? 'closed' : g.sig}`;
    })
    .join(' · ');
}
